import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';

import { useAppContext } from '../AppContext';
import { useLocationFilter } from '../context/LocationFilterContext';
import LoadingSpinner from './LoadingSpinner';
import NexusHeader from './NexusHeader';
import NexusFeed from './NexusFeed';
import NexusChat from './NexusChat';
import NexusDreamWeave from './NexusDreamWeave';
import NexusQuests from './NexusQuests';
import NexusMembers from './NexusMembers';
import { useTranslation } from 'react-i18next';


function NexusHub({ nexusId }) {
    const { t } = useTranslation();
    const { appFunctions, setMessage, handleUserSelect, userId, userProfiles, handlePageChange, LucideIcons } = useAppContext();
    const { isLocationFilterActive, locationFilterRadius, userLocation } = useLocationFilter();
    const [hubData, setHubData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('feed');

    const fetchHubData = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        if (isLocationFilterActive && !userLocation) {
            setError(t('nexusHub.locationError'));
            setIsLoading(false);
            setHubData(null);
            return;
        }

        const getNexusHubData = httpsCallable(appFunctions, 'getNexusHubData');
        try {
            const params = { nexusId };
            if (isLocationFilterActive && userLocation) {
                params.latitude = userLocation.latitude;
                params.longitude = userLocation.longitude;
                params.radius = locationFilterRadius;
            }
            const result = await getNexusHubData(params);
            setHubData(result.data);
        } catch (err) {
            console.error("Error fetching Nexus Hub data:", err);
            setError(err.message);
            if (err.code === 'permission-denied') {
                setTimeout(() => handlePageChange('nexus'), 3000);
            }
        } finally {
            setIsLoading(false);
        }
    }, [nexusId, appFunctions, handlePageChange, isLocationFilterActive, userLocation?.latitude, userLocation?.longitude, locationFilterRadius]);

    useEffect(() => {
        fetchHubData();
    }, [fetchHubData]);

    const FeedHeader = () => {
        if (isLocationFilterActive && activeTab === 'feed') {
            return (
                <div className="text-center my-4 p-2 bg-blue-900/50 text-blue-200 rounded-lg text-sm">
                    <p>{t('nexusHub.showingPosts', { radius: locationFilterRadius })}</p>
                </div>
            );
        }
        return null;
    };

    if (isLoading) return <LoadingSpinner message={t('nexusHub.loading')} />;

    if (error) return <div className="text-center text-red-400 p-8 bg-red-900/20 rounded-lg">{t('nexusHub.error', { error })}</div>;

    if (!hubData || !hubData.nexusData) {
        if (isLocationFilterActive) {
            return (
                <div className="text-center text-gray-400 p-8">
                    {t('nexusHub.noLocalPosts', { radius: locationFilterRadius })}
                </div>
            );
        }
        return <div className="text-center text-gray-400 p-8">{t('nexusHub.notFound')}</div>;
    }

    const { nexusData, posts, members, chat, activeQuests } = hubData;
    const isMember = nexusData.memberIds?.includes(userId) ?? false;

    return (
        <div className="animate-fadeIn">
            <NexusHeader nexus={nexusData} nexusId={nexusId} />
            
            {/* Gamified Sci-Fi Navigation Tabs */}
            <div className="flex justify-start md:justify-center overflow-x-auto custom-scrollbar my-8 pb-4 px-2">
                <div className="flex gap-3 p-1.5 bg-black/40 rounded-2xl border border-white/5 shadow-inner">
                    {[
                        { id: 'feed', label: t('nexusHub.tabs.feed'), icon: LucideIcons.Activity },
                        { id: 'chat', label: t('nexusHub.tabs.chat'), icon: LucideIcons.MessageSquare },
                        { id: 'dreamweave', label: t('nexusHub.tabs.dreamweave'), icon: LucideIcons.Sparkles },
                        { id: 'showdown', label: 'Showdown', icon: LucideIcons.Swords },
                        { id: 'quests', label: t('nexusHub.tabs.quests'), icon: LucideIcons.Target },
                        { id: 'members', label: t('nexusHub.tabs.members'), icon: LucideIcons.Users },
                        { id: 'relics', label: 'Relics', icon: LucideIcons.Trophy }
                    ].map(tab => (
                        <button 
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)} 
                            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 whitespace-nowrap ${
                                activeTab === tab.id 
                                ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-[0_0_20px_rgba(139,92,246,0.4)] scale-105 border border-white/20' 
                                : 'text-gray-400 hover:text-white hover:bg-white/10'
                            }`}
                        >
                            <tab.icon size={16} className={activeTab === tab.id ? 'animate-pulse' : ''} />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <FeedHeader />
            <div className="pb-20 md:pb-8">
                {activeTab === 'feed' && <NexusFeed initialPosts={posts} isMember={isMember} nexusId={nexusId} />}
                {activeTab === 'chat' && <NexusChat initialMessages={chat} isMember={isMember} nexusId={nexusId} />}
                {activeTab === 'dreamweave' && <NexusDreamWeave nexusId={nexusId} nexusData={nexusData} />}
                {activeTab === 'showdown' && (
                    <div className="max-w-3xl mx-auto p-6">
                        <div className="bg-gradient-to-br from-slate-900/90 to-black/95 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center shadow-2xl relative overflow-hidden group">
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-cyan-500/20 via-purple-500/5 to-transparent opacity-50 group-hover:opacity-70 transition-opacity duration-1000" />
                            <div className="relative z-10">
                                <div className="relative w-24 h-24 mx-auto mb-6">
                                    <div className="absolute inset-0 bg-cyan-500 rounded-full blur-2xl opacity-40 animate-pulse-slow"></div>
                                    <LucideIcons.Swords size={64} className="relative z-10 mx-auto text-cyan-400 drop-shadow-[0_0_15px_rgba(34,211,238,0.8)]" />
                                </div>
                                <h2 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-500 font-orbitron mb-4 tracking-wider">Showdown Arena</h2>
                                <p className="text-gray-300 mb-8 max-w-xl mx-auto text-lg leading-relaxed">
                                    Represent <span className="text-cyan-400 font-bold">{nexusData.name}</span> in high-stakes duels. Win souls, climb the leaderboard, and bring glory to your faction.
                                </p>
                                <button 
                                    onClick={() => handlePageChange('showdown')}
                                    className="px-10 py-4 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-black rounded-xl shadow-[0_0_30px_rgba(34,211,238,0.3)] transition-all transform hover:scale-110 border border-cyan-400/50 flex items-center gap-3 mx-auto uppercase tracking-widest text-sm"
                                >
                                    <LucideIcons.Crosshair size={20} className="animate-pulse" />
                                    Enter the Arena
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === 'quests' && <NexusQuests quests={activeQuests} />}
                {activeTab === 'members' && <NexusMembers members={members} nexus={nexusData} onUserSelect={handleUserSelect} userProfiles={userProfiles} />}
                {activeTab === 'relics' && (
                    <div className="max-w-4xl mx-auto p-4 md:p-8">
                        <div className="text-center mb-10">
                            <LucideIcons.Trophy size={48} className="mx-auto text-amber-400 mb-4 drop-shadow-[0_0_15px_rgba(251,191,36,0.6)] animate-pulse-slow" />
                            <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-500 font-serif mb-2">The Relic Room</h2>
                            <p className="text-gray-400">Trophies, artifacts, and glory earned by {nexusData.name}.</p>
                        </div>
                        {(!nexusData.relics || nexusData.relics.length === 0) ? (
                            <div className="bg-gray-900/50 border border-dashed border-gray-700 rounded-2xl p-12 text-center flex flex-col items-center">
                                <LucideIcons.Ghost size={48} className="text-gray-600 mb-4" />
                                <h3 className="text-xl font-bold text-gray-500 mb-2">The Vault is Empty</h3>
                                <p className="text-gray-600 max-w-md">Your faction has not yet claimed any relics. Win Showdown tournaments or complete Elite Quests to fill this vault.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                                {nexusData.relics.map((relic, idx) => (
                                    <div key={idx} className="bg-gradient-to-b from-gray-800 to-gray-900 border border-amber-500/30 rounded-xl p-6 text-center hover:scale-105 transition-transform hover:shadow-[0_0_30px_rgba(251,191,36,0.2)]">
                                        <div className="text-4xl mb-4 drop-shadow-md">{relic.icon || '🏆'}</div>
                                        <h4 className="font-bold text-amber-300 mb-1">{relic.name}</h4>
                                        <p className="text-xs text-gray-400">{relic.description}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default NexusHub;